import React, { useEffect, useState, useRef, memo } from 'react';
import { Map } from 'immutable';
import { bindActionCreators } from 'redux';
import { connect } from 'react-redux';
import moment from 'moment';
import { push } from 'connected-react18-router';

import { unwrap } from '../../utils/ChangeSpy';
import * as ActionType from '../../actions/meetings';
import { fetchEventActivities } from '../../actions/eventActivities';
import Popups from '../../components/Popup';
import AutoCompleteContainer from '../AutoComplete';
import { convertDateString, dateToString } from '../../utils/dateFormat';
import downloadFile from '../../utils/downloadFile';
import { showError } from '../../utils/MessagePopup';
import { usePopupContext } from '../../contexts/PopupContext';
import { IconButton } from '../../components/helpers/IconButton';

const MeetingDetailsPopupContainer = props => {
  const { closePopup, openPopup } = usePopupContext();
  const meetingId = props.popup.getIn(['props', 'meetingId']);
  const company = props.popup.getIn(['props', 'company']);
  const loadWidgetData = props.popup.getIn(['props', 'loadWidgetData']);
  const [showErrorModal, setShowErrorModal] = useState(null);
  const [companyType, setCompanyType] = useState(company === '' || !company ? 'target' : company.get('companyType'));
  const savedActivityMapTextRef = useRef(null);
  const activityMapText = unwrap(props.meeting.getIn(['activityMap', 'text']));

  useEffect(() => {
    props.fetchEventActivities();
    props.fetchMeetingById({ id: meetingId, user: props.user });
    props.changeField({ name: 'current.companyType', value: companyType });

    // Prefill target info if company is provided.
    if (company) {
      props.fetchEventInfoById({ id: company.get('id') });
      props.changeField({ name: 'current.targetId', value: company.get('id') });
      props.changeField({ name: 'current.targetName', value: company.get('legalName') });
    }
  }, []);

  useEffect(
    () => () => {
      props.resetForm();
    },
    [],
  );

  useEffect(() => {
    const { meeting, fetchCompetedEvents } = props;

    if (meeting.getIn(['targetId', 'value']) && props.meeting.getIn(['type', 'value']) === 'update_completed_action') {
      fetchCompetedEvents({
        companyId: unwrap(meeting.get('targetId', '')),
        companyType,
      });
    }
  }, [props.meeting.getIn(['targetId', 'value']), props.meeting.getIn(['type', 'value'])]);

  const handleSuggestChange = ({ name, value }) => {
    if (name === 'current.targetName' && value === '') {
      props.changeField({ name: 'suggestCompletedEvent', value: undefined });
    }

    props.changeField({ name, value });
  };

  const handleEventInfoById = suggestion => {
    props.fetchEventInfoById({ id: suggestion.id });
  };

  const getTargetSuggestions = () => {
    const prefix = 'current.';

    return (
      <AutoCompleteContainer
        idName={`${prefix}targetId`}
        inputProps={{
          name: 'targetId',
          className: 'form-control',
          label: (
            <>
              Target&nbsp;
              {!isDisabledAction && (
                <IconButton
                  href={`/company/${props.meeting.get('id')}`}
                  icon="reply"
                  size="sm"
                  title="Open company in a new tab"
                />
              )}
            </>
          ),
          placeholder: 'Target',
        }}
        name={`${prefix}targetName`}
        onChange={handleSuggestChange}
        onFind={props.fetchTargetEventInfo}
        onSelectCallback={handleEventInfoById}
        rootPath={['meetings']}
        suggestionsName="suggestTarget"
        valueName={`${prefix}targetName`}
      />
    );
  };

  const getBuyerSuggestions = () => {
    const prefix = 'current.';

    return (
      <AutoCompleteContainer
        idName={`${prefix}targetId`}
        inputProps={{
          name: 'targetId',
          className: 'form-control',
          label: (
            <>
              Buyer&nbsp;
              {!isDisabledAction && (
                <IconButton
                  href={`/company/${props.meeting.get('id')}`}
                  icon="reply"
                  size="sm"
                  title="Open company in a new tab"
                />
              )}
            </>
          ),
          placeholder: 'Buyer',
        }}
        name={`${prefix}targetName`}
        onChange={handleSuggestChange}
        onFind={props.fetchBuyerEventInfo}
        onSelectCallback={handleEventInfoById}
        rootPath={['meetings']}
        suggestionsName="suggestTarget"
        valueName={`${prefix}targetName`}
      />
    );
  };

  const getTeamSuggestions = () => {
    const prefix = 'current.';

    return (
      <AutoCompleteContainer
        idName={`${prefix}recordOwnerId`}
        inputProps={{
          name: 'recordOwnerId',
          placeholder: 'Module',
          className: 'form-control',
          label: 'Team',
        }}
        name={`${prefix}recordOwnerName`}
        onChange={handleSuggestChange}
        onFind={props.findUsers}
        rootPath={['meetings']}
        suggestionsName="suggestUser"
        valueName={`${prefix}recordOwnerName`}
      />
    );
  };

  const getAnalystSuggestions = () => {
    const prefix = 'current.';

    return (
      <AutoCompleteContainer
        idName={`${prefix}recordSubOwnerId`}
        inputProps={{
          name: 'recordSubOwnerId',
          label: 'Analyst',
          placeholder: 'Analyst',
          className: 'form-control',
        }}
        name={`${prefix}recordSubOwnerName`}
        onChange={handleSuggestChange}
        onFind={props.findUsers}
        rootPath={['meetings']}
        suggestionsName="suggestUser"
        valueName={`${prefix}recordSubOwnerName`}
      />
    );
  };

  const getDealmakerSuggestions = () => {
    const prefix = 'current.';

    return (
      <AutoCompleteContainer
        idName={`${prefix}dealMakerId`}
        inputProps={{
          name: 'dealMakerId',
          label: 'Dealmaker',
          placeholder: 'Dealmaker',
          className: 'form-control',
        }}
        name={`${prefix}dealMakerName`}
        onChange={handleSuggestChange}
        onFind={props.findUsers}
        rootPath={['meetings']}
        suggestionsName="suggestUser"
        valueName={`${prefix}dealMakerName`}
      />
    );
  };

  const getCompletedBySuggestions = () => {
    const prefix = 'current.';

    return (
      <AutoCompleteContainer
        idName={`${prefix}userId`}
        inputProps={{
          name: 'userId',
          placeholder: 'Completed By',
          className: 'form-control',
          label: 'Completed By',
        }}
        name={`${prefix}userName`}
        onChange={handleSuggestChange}
        onFind={props.findUsers}
        rootPath={['meetings']}
        suggestionsName="suggestUser"
        valueName={`${prefix}userName`}
      />
    );
  };

  const handleClose = () => {
    closePopup();
  };

  const handleChangeTab = currentType => {
    if (currentType === companyType) {
      return;
    }

    setCompanyType(currentType);

    if (currentType === company.get('companyType')) {
      props.fetchEventInfoById({ id: company.get('id') });
      props.changeField({ name: 'current.companyType', value: currentType });
      props.changeField({ name: 'current.type', value: null });
      props.changeField({ name: 'current.targetId', value: company.get('id') });
      props.changeField({ name: 'current.targetName', value: company.get('legalName') });
      props.changeField({
        name: 'current.activityMap.text',
        value: savedActivityMapTextRef.current ?? activityMapText,
      });
      savedActivityMapTextRef.current = null;
    } else {
      savedActivityMapTextRef.current = activityMapText;
      props.changeField({ name: 'current.companyType', value: currentType });
      props.changeField({ name: 'current.type', value: null });
      props.changeField({ name: 'current.contactId', value: 0 });
      props.changeField({ name: 'current.contacts', value: [] });
      props.changeField({ name: 'current.activityMap.text', value: '' });
      props.changeField({ name: 'current.targetId', value: '' });
      props.changeField({ name: 'current.targetName', value: '' });
    }
  };

  const handleChange = event => {
    const { name, value } = event.target;

    if (name === 'type') {
      if (value === 'update_completed_action' && unwrap(props.meeting.get('targetId'))) {
        props.fetchCompetedEvents({
          companyId: unwrap(props.meeting.get('targetId', '')),
          companyType,
        });
      }

      return props.changeField({ name: 'current.type', value });
    }

    if (name === 'eventId') {
      return props.changeField({ name: 'current.eventId', value });
    }

    if (name === 'contactId') {
      return props.changeField({ name: 'current.contactId', value });
    }

    if (name === 'title') {
      return props.changeField({ name: 'current.title', value });
    }

    if (name === 'dealNotes') {
      return props.changeField({ name: 'current.dealNotes', value });
    }

    if (name === 'date') {
      return props.changeField({ name: 'current.date', value: moment(value) });
    }

    props.changeField({ name, value });
  };

  const handleSave = () => {
    const { meeting, createMeeting } = props;
    const body = {
      meetingId: unwrap(meeting.get('meetingId')),
      buyerId: companyType === 'target' ? unwrap(meeting.get('buyerId')) : unwrap(meeting.get('targetId')),
      projectId: unwrap(meeting.get('projectId')),
      contactId: unwrap(meeting.get('contactId')),
      dealMakerId: unwrap(meeting.get('dealMakerId')),
      targetId: unwrap(meeting.get('targetId')),
      recordOwnerId: unwrap(meeting.get('recordOwnerId')),
      recordSubOwnerId: unwrap(meeting.get('recordSubOwnerId')),
      activity: unwrap(meeting.getIn(['activityMap', 'text'])),
      userId: unwrap(meeting.get('userId')),
      type: unwrap(meeting.get('type')),
      description: unwrap(meeting.get('title')),
      dealNotes: unwrap(meeting.get('dealNotes')),
      firefliesSummaryHtml: unwrap(meeting.get('summaryHtml')),
      date: dateToString(unwrap(meeting.get('date'))),
      completed: true,
      eventId: meeting.get('eventId'),
      companyType,
    };

    createMeeting({
      body,
      afterSuccess: () => {
        if (unwrap(meeting.get('type')) === 'complete_next_action') {
          window.open(`/company/${unwrap(meeting.get('targetId'))}/${companyType}?createNewEvent`, '_blank');
        }
        closePopup();
        loadWidgetData({ name: 'meetings' });
      },
      afterError: ({ responseBody }) => {
        const modal = showError(openPopup, responseBody, () => setShowErrorModal(null), undefined, true);

        setShowErrorModal(modal);
      },
    });
  };

  const handleDownload = () => {
    const url = props.meeting.get('meetingLink');
    const name = new URL(url).pathname.split('/').pop();

    downloadFile({ url, name, noAuth: true });
  };

  const handleActivityChange = data => {
    props.changeField({
      name: 'current.activityMap',
      value: data,
    });
  };

  const isDisabledAction =
    unwrap(props.meeting.get('targetId', '')) === '' || !unwrap(props.meeting.get('targetId', ''));
  const types = props.suggestCompletedEvent?.map(event => {
    const id = event.get('id');
    const activity =
      event.get('description') === ''
        ? `${event.get('activity')}`
        : `${event.get('activity')} - ${event.get('description')}`;
    const text = `${convertDateString(event.get('date'))} ${activity}`;

    return Map({
      id,
      text,
    });
  });

  return (
    <div>
      {showErrorModal}
      <Popups.MeetingDetailsPopup
        activities={props.activities}
        companyType={companyType}
        isDisabledAction={isDisabledAction}
        isLoading={props.isLoading}
        isLoadingCompletedEvents={props.isLoadingCompletedEvents}
        meeting={props.meeting}
        onActivityChange={handleActivityChange}
        onChange={handleChange}
        onChangeTab={handleChangeTab}
        onClose={handleClose}
        onDownload={handleDownload}
        onSave={handleSave}
        suggestAnalyst={getAnalystSuggestions()}
        suggestBuyer={getBuyerSuggestions()}
        suggestCompletedBy={getCompletedBySuggestions()}
        suggestDealmaker={getDealmakerSuggestions()}
        suggestTarget={getTargetSuggestions()}
        suggestTeam={getTeamSuggestions()}
        types={types}
      />
    </div>
  );
};

const mapStateToProps = (state, props) => ({
  ...props,
  meeting: state.meetings.get('current'),
  isLoading: state.meetings.get('isLoading'),
  isLoadingCompletedEvents: state.meetings.get('isLoadingCompletedEvents'),
  suggestCompletedEvent: state.meetings.get('suggestCompletedEvent'),
  user: state.auth.get('user', null),
  activities: state.eventActivities.getIn(['activities', 'meetingActivities']),
});

const mapDispatchToProps = dispatch => ({
  ...bindActionCreators(ActionType, dispatch),
  fetchEventActivities: () => dispatch(fetchEventActivities()),
  push,
});

export { MeetingDetailsPopupContainer };
export default connect(mapStateToProps, mapDispatchToProps)(memo(MeetingDetailsPopupContainer));
